package com.zq.webproject.network.network
import com.zq.webproject.getPlatform
import com.zq.webproject.model.AddBankCardResponse
import com.zq.webproject.model.AppVersion
import com.zq.webproject.model.ApplyWithdrawResponse
import com.zq.webproject.model.ComplaintResponse
import com.zq.webproject.model.CreateOrderResponse
import com.zq.webproject.model.IncomeRecordResponse
import com.zq.webproject.model.InvitedUsersResponse
import com.zq.webproject.model.OrderDetailResponse
import com.zq.webproject.model.ProductDetailResponse
import com.zq.webproject.model.PurchaseRecordResponse
import com.zq.webproject.model.RankingResponse
import com.zq.webproject.model.WithdrawRecordListResponse
import com.zq.webproject.network.model.BalanceResponse
import com.zq.webproject.network.model.BannerResponse
import com.zq.webproject.network.model.CategoryResponse
import com.zq.webproject.network.model.EarningsResponse
import com.zq.webproject.network.model.LoginResponse
import com.zq.webproject.network.model.ProductResponse
import com.zq.webproject.network.model.RechargeResponse
import com.zq.webproject.network.model.RegisterResponse
import com.zq.webproject.network.model.SendCodeResponse
import com.zq.webproject.network.model.UpgradeResponse
import com.zq.webproject.network.model.UploadProductRequest
import com.zq.webproject.network.model.UploadProductResponse
import com.zq.webproject.network.model.UserListResponse
import com.zq.webproject.network.model.UserRoleResponse
import com.zq.webproject.utils.TokenManager
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import org.example.project.model.AlipayResponse
import org.example.project.model.PaymentResponse

class NetworkService {
 // 基础URL - Web平台使用代理路径，其他平台使用完整URL
 private val baseUrl: String
  get() = if (getPlatform().name.contains("Web", ignoreCase = true)) {
   "/api" // Web平台通过webpack代理访问
  } else {
   "https://shop.aiotzq.com/api" // Android等原生平台直接访问
  }

 // Ktor HttpClient实例，配置JSON序列化
 private val client = HttpClient {
  install(ContentNegotiation) {
   json(Json {
    ignoreUnknownKeys = true // 忽略接口返回的未知字段
    explicitNulls = false     // 不序列化null值
    coerceInputValues = true  // 强制转换输入值
   })
  }
 }

 /**
  * 获取设备ID（用于API请求）
  */
 private fun getDeviceIdForRequest(): String? {
  return try {
   com.zq.webproject.utils.DeviceUtils.getDeviceId()
  } catch (e: Exception) {
   println("NetworkService - 获取设备ID失败: ${e.message}")
   null
  }
 }
 
 /**
  * 扩展函数：为HTTP请求自动添加设备ID请求头
  */
 private fun  HttpRequestBuilder.addDeviceId() {
  getDeviceIdForRequest()?.let { deviceId ->
   header("X-Device-ID", deviceId)
  }
 }

 /**
  * 检查响应是否为 401 (Token失效)
  * 如果是401，清除 Token 并触发登出回调
  */
 private suspend fun checkUnauthorized(response: HttpResponse) {
  if (response.status.value == 401) {
   println("NetworkService - 检测到401错误，Token失效")
   
   // 尝试解析错误消息
   try {
    val bodyText = response.bodyAsText()
    println("NetworkService - 401响应内容: $bodyText")
   } catch (e: Exception) {
    println("NetworkService - 无法读取401响应内容: ${e.message}")
   }
   
   // 清除 Token
   TokenManager.clearToken()
   
   // 触发 Token 失效回调（自动退出登录）
   TokenManager.triggerTokenInvalid()
  }
 }


 /**
  * 发送验证码接口
  * POST /users/sendCode
  * 参数通过Query传递: phone
  * @param phone 手机号
  * @return Result<SendCodeResponse> 发送结果
  */
 suspend fun sendCode(phone: String): Result<SendCodeResponse> {
  return try {
   val sendCodeUrl = "$baseUrl/users/sendCode?phone=$phone"
   println("NetworkService - 请求发送验证码: $sendCodeUrl") // 日志输出
   println("NetworkService - 参数: phone=$phone") // 日志输出

   val response = client.post(sendCodeUrl) {
    // 参数通过URL传递，不需要设置Body
   }.body<SendCodeResponse>()

   // 检查发送是否成功
   if (response.code == 200) {
    println("NetworkService - 验证码发送成功！") // 日志输出
    println("NetworkService - 响应信息: ${response.message}") // 日志输出
   } else {
    println("NetworkService - 验证码发送失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 发送验证码异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 注册接口
  * POST /users/register
  * 参数通过Query传递: phone, password, code, invitationCode(非必填), deviceId, deviceName, deviceType
  * @param phone 手机号
  * @param password 密码
  * @param code 验证码
  * @param invitationCode 邀请码(非必填)
  * @param deviceId 设备唯一标识
  * @param deviceName 设备名称（可选）
  * @param deviceType 设备类型（可选）
  * @return Result<RegisterResponse> 注册结果
  */
 suspend fun register(
  phone: String,
  password: String,
  code: String,
  invitationCode: String? = null,
  deviceId: String,
  deviceName: String? = null,
  deviceType: String? = null
 ): Result<RegisterResponse> {
  return try {
   // 构建URL，添加设备信息参数
   val registerUrl = buildString {
    append("$baseUrl/users/register?phone=$phone&password=$password&code=$code")
    if (!invitationCode.isNullOrEmpty()) {
     append("&invitationCode=").append(invitationCode)
    }
    append("&deviceId=").append(deviceId)
    if (!deviceName.isNullOrEmpty()) {
     append("&deviceName=").append(deviceName)
    }
    if (!deviceType.isNullOrEmpty()) {
     append("&deviceType=").append(deviceType)
    }
   }
   
   println("NetworkService - 请求注册: $registerUrl") // 日志输出
   println("NetworkService - 参数: phone=$phone, password=******, code=$code, invitationCode=$invitationCode, deviceId=$deviceId, deviceName=$deviceName, deviceType=$deviceType") // 日志输出

   val response = client.post(registerUrl) {
    // 参数通过URL传递，不需要设置Body
   }.body<RegisterResponse>()

   // 检查注册是否成功
   if (response.code == 200) {
    println("NetworkService - 注册成功！") // 日志输出
    println("NetworkService - 注册信息: ${response.message}") // 日志输出
   } else {
    println("NetworkService - 注册失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 注册异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 登录接口
  * POST /users/login
  * 参数通过Query传递：phone, password, deviceId, deviceName, deviceType
  * @param username 用户名（手机号）
  * @param password 密码
  * @param deviceId 设备唯一标识
  * @param deviceName 设备名称（可选）
  * @param deviceType 设备类型（可选）
  * @return Result<LoginResponse> 登录结果
  */
 suspend fun login(
  username: String, 
  password: String,
  deviceId: String,
  deviceName: String? = null,
  deviceType: String? = null
 ): Result<LoginResponse> {
  return try {
   // 构建URL，添加设备信息参数
   val loginUrl = buildString {
    append("$baseUrl/users/login?phone=$username&password=$password&deviceId=$deviceId")
    if (!deviceName.isNullOrEmpty()) {
     append("&deviceName=").append(deviceName)
    }
    if (!deviceType.isNullOrEmpty()) {
     append("&deviceType=").append(deviceType)
    }
   }
   
   println("NetworkService - 请求登录: $loginUrl") // 日志输出
   println("NetworkService - 参数: phone=$username, password=******, deviceId=$deviceId, deviceName=$deviceName, deviceType=$deviceType") // 日志输出

   val response = client.post(loginUrl) {
    // 参数通过URL传递，不需要设置Body
   }.body<LoginResponse>()

   // 检查登录是否成功
   if (response.code==200 && response.data?.token != null) {
    // 登录成功后保存Token、用户名和邀请码
    val displayName = response.data?.user?.phoneNumber ?: username
    val referralCode = response.data?.user?.referralCode
    TokenManager.saveToken(response.data.token, displayName, referralCode)
    println("NetworkService - 登录成功！") // 日志输出
    println("NetworkService - Token已保存: ${response.data.token}") // 日志输出
    println("NetworkService - 用户信息: $displayName, 余额: ${response.data?.user?.balance}, 邀请码: $referralCode") // 日志输出
   } else {
    println("NetworkService - 登录失败: ${response.message}") // 日志输出

   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 登录异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 获取用户列表接口
  * GET /users
  * @param token 用户Token (Authorization: Bearer token)
  * @return Result<UserListResponse> 用户列表结果
  */
 suspend fun getUsers(token: String): Result<UserListResponse> {
  return try {
   println("NetworkService - 请求用户列表: $baseUrl/users") // 日志输出
   val response = client.get("$baseUrl/users") {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<UserListResponse>()
   println("NetworkService - 获取用户列表成功，共 ${response.data.size} 个用户") // 日志输出
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取用户列表失败: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 获取用户信息接口
  * @param token 用户登录后的Token
  * @return Result<String> 用户信息结果（成功返回用户信息字符串，失败返回异常）
  */
 suspend fun getUserProfile(token: String): Result<String> {
  return try {
   println("NetworkService - 请求用户信息: $baseUrl/users/profile") // 日志输出
   val response = client.get("$baseUrl/users/profile") {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<String>()
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取用户信息失败: ${e.message}") // 日志输出
   Result.failure(e)
  }
 }


 /**
  * 获取商品分类接口
  * @param token 用户Token（接口可选，实际按需求传递）
  * @return Result<CategoryResponse> 分类结果（成功返回分类列表，失败返回异常）
  */
 suspend fun getCategories(token: String): Result<CategoryResponse> {
  return try {
   println("NetworkService - 请求商品分类: $baseUrl/categories") // 日志输出
   val response = client.get("$baseUrl/categories") {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<CategoryResponse>()
   println("NetworkService - 获取分类成功，数量: ${response.data?.size ?: 0}") // 日志输出
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取分类失败: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 获取商品列表接口（支持分页）
  * @param token 用户Token（接口可选）
  * @param page 页码（默认1）
  * @param pageSize 每页数量（默认100）
  * @return Result<ProductResponse> 商品列表结果
  */
 suspend fun getProducts(
  token: String,
  page: String = "1",
  pageSize: String = "100"
 ): Result<ProductResponse> {
  return try {
   val url = "$baseUrl/products?page=$page&pageSize=$pageSize"
   println("NetworkService - 请求商品列表: $url") // 日志输出
   val response = client.get(url) {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<ProductResponse>()
   println("NetworkService - 获取商品成功，数量: ${response.data?.size ?: 0}") // 日志输出
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取商品列表失败: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 获取首页Banner图接口
  * @param token 用户Token（接口可选）
  * @return Result<BannerResponse> Banner列表结果
  */
 suspend fun getHomeBanners(token: String): Result<BannerResponse> {
  return try {
   // 尝试多个可能的路径
   println("NetworkService - 请求Banner: $baseUrl/home/banners") // 日志输出
   val response = client.get("$baseUrl/home/banners") {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<BannerResponse>()
   println("NetworkService - 获取Banner成功，数量: ${response.data?.size ?: 0}") // 日志输出
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取Banner失败: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 检查应用版本更新接口
  * 直接请求远程JSON文件
  * @return Result<AppVersion> 版本信息结果
  */
 suspend fun checkAppVersion(): Result<AppVersion> {
  return try {
   val versionUrl = "https://updata.aiotzq.com/web_project.json"
   println("NetworkService - 检查应用版本: $versionUrl") // 日志输出
   val response = client.get(versionUrl).body<AppVersion>()
   println("NetworkService - 获取版本信息成功: version=${response.version}") // 日志输出
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 检查版本失败: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 根据分类ID筛选商品接口（支持分页）
  * GET /products/category?categoryId={categoryId}&page={page}&pageSize={pageSize}
  * @param token 用户Token（接口可选）
  * @param categoryId 分类ID
  * @param page 页码（默认1）
  * @param pageSize 每页数量（默认50）
  * @return Result<ProductResponse> 对应分类的商品列表
  */
 suspend fun getProductsByCategory(
  token: String,
  categoryId: String,
  page: String = "1",
  pageSize: String = "50"
 ): Result<ProductResponse> {
  return try {
   val url = "$baseUrl/products/category?categoryId=$categoryId&page=$page&pageSize=$pageSize"
   println("NetworkService - 请求分类商品: $url") // 日志输出
   val response = client.get(url) {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<ProductResponse>()
   println("NetworkService - 获取分类商品成功，数量: ${response.data?.size ?: 0}") // 日志输出
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取分类商品失败: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 关闭HttpClient连接（避免资源泄漏）
  */
 fun close() {
  client.close()
 }


 /**
  * 获取用户余额接口
  * GET /users/balance
  * @param token 用户Token (Authorization: Bearer token)
  * @return Result<BalanceResponse> 余额结果
  */
 suspend fun getUserBalance(token: String): Result<BalanceResponse> {
  return try {
   val deviceId = getDeviceIdForRequest()
   println("NetworkService - 请求用户余额: $baseUrl/users/balance, deviceId=$deviceId") // 日志输出
   val response = client.get("$baseUrl/users/balance") {
    header("Authorization", "Bearer $token")
    deviceId?.let { header("X-Device-ID", it) }
   }
   
   // 检查是否为 401 错误
   checkUnauthorized(response)
   
   val balanceResponse = response.body<BalanceResponse>()

   if (balanceResponse.code == 200) {
    println("NetworkService - 获取用户余额成功: ${balanceResponse.data?.balance}") // 日志输出
   } else {
    println("NetworkService - 获取用户余额失败: ${balanceResponse.message}") // 日志输出
   }

   Result.success(balanceResponse)
  } catch (e: Exception) {
   println("NetworkService - 获取用户余额异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 充值接口
  * POST /users/recharge?amount={amount}&paymentMethod={paymentMethod}
  * @param token 用户Token (Authorization: Bearer token)
  * @param amount 充值金额
  * @param paymentMethod 支付方式：alipay(支付宝支付)
  * @return Result<RechargeResponse> 充值结果
  */
 suspend fun rechargeBalance(token: String, amount: Double, paymentMethod: String = "alipay"): Result<RechargeResponse> {
  return try {
   val url = "$baseUrl/users/recharge?amount=$amount&paymentMethod=$paymentMethod"
   println("NetworkService - 请求充值: $url, 金额: $amount, 支付方式: $paymentMethod") // 日志输出
   val response = client.post(url) {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<RechargeResponse>()

   if (response.code == 200 || response.code == 1) {
    println("NetworkService - 充值订单创建成功: ${response.message}") // 日志输出
   } else {
    println("NetworkService - 充值失败: code=${response.code}, message=${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 充值异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 升级为加盟商接口
  * POST /users/upgrade-to-affiliate?paymentMethod={paymentMethod}
  * @param token 用户Token (Authorization: Bearer token)
  * @param paymentMethod 支付方式：balance(余额支付) 或 alipay(支付宝支付)
  * @return Result<UpgradeResponse> 升级结果
  */
 suspend fun upgradeToAffiliate(token: String, paymentMethod: String = "balance"): Result<UpgradeResponse> {
  return try {
   val url = "$baseUrl/users/upgrade-to-affiliate?paymentMethod=$paymentMethod"
   println("NetworkService - 请求升级为加盟商: $url, 支付方式: $paymentMethod") // 日志输出
   val response = client.post(url) {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<UpgradeResponse>()
 
   if (response.code == 200) {
    println("NetworkService - 升级成功: ${response.message}") // 日志输出
   } else {
    println("NetworkService - 升级失败: code=${response.code}, message=${response.message}") // 日志输出
   }
 
   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 升级为加盟商异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }


 /**
  * 获取用户角色接口
  * GET /users/role
  * @param token 用户Token (Authorization: Bearer token)
  * @return Result<UserRoleResponse> 角色结果
  */
 suspend fun getUserRole(token: String): Result<UserRoleResponse> {
  return try {
   val deviceId = getDeviceIdForRequest()
   println("NetworkService - 请求用户角色: $baseUrl/users/role, deviceId=$deviceId") // 日志输出
   val response = client.get("$baseUrl/users/role") {
    header("Authorization", "Bearer $token")
    deviceId?.let { header("X-Device-ID", it) }
   }.body<UserRoleResponse>()

   if (response.code == 200) {
    println("NetworkService - 获取用户角色成功: ${response.data?.role}") // 日志输出
   } else {
    println("NetworkService - 获取用户角色失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取用户角色异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 获取用户总收益接口
  * GET /users/earnings
  * @param token 用户Token (Authorization: Bearer token)
  * @return Result<EarningsResponse> 总收益结果
  */
 suspend fun getUserEarnings(token: String): Result<EarningsResponse> {
  return try {
   val deviceId = getDeviceIdForRequest()
   println("NetworkService - 请求用户总收益: $baseUrl/users/earnings, deviceId=$deviceId") // 日志输出
   val response = client.get("$baseUrl/users/earnings") {
    header("Authorization", "Bearer $token")
    deviceId?.let { header("X-Device-ID", it) }
   }.body<EarningsResponse>()

   if (response.code == 200) {
    println("NetworkService - 获取用户总收益成功: ${response.data?.totalEarnings}") // 日志输出
   } else {
    println("NetworkService - 获取用户总收益失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取用户总收益异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }
 /**
  * 获取商品详情接口
  * GET /products/detail?productId={id}
  * @param productId 商品ID
  * @return Result<ProductDetailResponse> 商品详情结果
  */
 suspend fun getProductDetail(productId: String): Result<ProductDetailResponse> {
  return try {
   val url = "$baseUrl/products/detail?productId=$productId"
   println("NetworkService - 请求商品详情: $url") // 日志输出
   val response = client.get(url).body<ProductDetailResponse>()

   if (response.code == 200) {
    println("NetworkService - 获取商品详情成功: ${response.data?.name}") // 日志输出
   } else {
    println("NetworkService - 获取商品详情失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取商品详情异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 创建订单接口
  * POST /users/create-order?productId={id}
  * @param productId 商品ID
  * @param token 用户Token
  * @return Result<CreateOrderResponse> 订单创建结果
  */
 suspend fun createOrder(productId: Int, token: String): Result<CreateOrderResponse> {
  return try {
   val url = "$baseUrl/users/create-order?productId=$productId"
   println("NetworkService - 请求创建订单: $url") // 日志输出
   println("NetworkService - 商品ID: $productId") // 日志输出

   val response = client.post(url) {
    header("Authorization", "Bearer $token")
   }.body<CreateOrderResponse>()

   if (response.code == 200) {
    println("NetworkService - 订单创建成功: 订单号=${response.data?.orderNo}") // 日志输出
   } else {
    println("NetworkService - 订单创建失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 创建订单异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 获取订单详情接口
  * GET /users/order-detail?orderNo={orderNo}
  * @param orderNo 订单号
  * @param token 用户Token
  * @return Result<OrderDetailResponse> 订单详情结果
  */
 suspend fun getOrderDetail(orderNo: String, token: String): Result<OrderDetailResponse> {
  return try {
   val url = "$baseUrl/users/order-detail?orderNo=$orderNo"
   println("NetworkService - 请求订单详情: $url") // 日志输出
   println("NetworkService - 订单号: $orderNo") // 日志输出

   val response = client.get(url) {
    header("Authorization", "Bearer $token")
   }.body<OrderDetailResponse>()

   if (response.code == 200) {
    println("NetworkService - 获取订单详情成功: 商品=${response.data?.productName}") // 日志输出
   } else {
    println("NetworkService - 获取订单详情失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 获取订单详情异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 余额支付接口
  * POST /users/pay-with-balance?amount={amount}&orderNo={orderNo}
  * @param amount 支付金额
  * @param orderNo 订单号
  * @param token 用户Token
  * @return Result<PaymentResponse> 支付结果
  */
 suspend fun payWithBalance(amount: Double, orderNo: String, token: String): Result<PaymentResponse> {
  return try {
   val url = "$baseUrl/users/pay-with-balance?amount=$amount&orderNo=$orderNo"
   println("NetworkService - 请求余额支付: $url") // 日志输出
   println("NetworkService - 支付金额: $amount, 订单号: $orderNo") // 日志输出

   val response = client.post(url) {
    header("Authorization", "Bearer $token")
   }.body<PaymentResponse>()

   if (response.code == 200) {
    println("NetworkService - 余额支付成功: 支付记录ID=${response.data?.paymentId}") // 日志输出
   } else {
    println("NetworkService - 余额支付失败: ${response.message}") // 日志输出gjh
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 余额支付异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 获取个人购买记录接口
  * GET /users/purchase-history
  * @return PurchaseRecordResponse 购买记录结果
  */
 suspend fun getUserPurchaseHistory(): PurchaseRecordResponse {
  val token = TokenManager.getToken() ?: ""
  println("NetworkService - 请求购买记录: $baseUrl/users/purchase-history") // 日志输出
  println("NetworkService - Token: $token") // 日志输出

  return try {
   val response = client.get("$baseUrl/users/purchase-history") {
    header("Authorization", "Bearer $token")
   }.body<PurchaseRecordResponse>()

   // 打印完整响应数据
   println("NetworkService - 购买记录接口响应成功") // 日志输出
   println("NetworkService - 响应码: ${response.code}") // 日志输出
   println("NetworkService - 响应消息: ${response.message}") // 日志输出
   println("NetworkService - 记录数量: ${response.data.size}") // 日志输出

   response
  } catch (e: Exception) {
   println("NetworkService - 请求购买记录异常: ${e.message}") // 日志输出
   e.printStackTrace()
   throw e
  }
 }

 /**
  * 添加银行卡接口
  * POST /users/bank-cards
  * 参数通过Query传递：cardNumber, cardHolderName, expirationDate, cvv, bankName
  * @param token 用户Token
  * @param cardNumber 银行卡号
  * @param cardHolderName 持卡人姓名
  * @param expirationDate 有效期
  * @param cvv CVV安全码
  * @param bankName 银行名称
  * @return Result<AddBankCardResponse> 添加结果
  */
 suspend fun addBankCard(
  token: String,
  cardNumber: String,
  cardHolderName: String,
  expirationDate: String,
  cvv: String,
  bankName: String
 ): Result<AddBankCardResponse> {
  return try {
   val url = "$baseUrl/users/bank-cards?cardNumber=$cardNumber&cardHolderName=$cardHolderName&expirationDate=$expirationDate&cvv=$cvv&bankName=$bankName"
   println("NetworkService - 请求添加银行卡: $url") // 日志输出
   println("NetworkService - 银行卡号: $cardNumber, 持卡人: $cardHolderName, 有效期: $expirationDate, 银行: $bankName") // 日志输出

   val response = client.post(url) {
    header("Authorization", "Bearer $token")
   }.body<AddBankCardResponse>()

   if (response.success) {
    println("NetworkService - 银行卡添加成功: ${response.message}") // 日志输出
    println("NetworkService - 银行卡ID: ${response.bankCard?.id}") // 日志输出
   } else {
    println("NetworkService - 银行卡添加失败: ${response.message}") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 添加银行卡异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 申请提现接口
  * POST /users/apply-withdraw
  * 参数通过Query传递：amount
  * @param token 用户Token
  * @param amount 提现金额
  * @return Result<ApplyWithdrawResponse> 申请结果
  */
 suspend fun applyWithdraw(token: String, amount: Double): Result<ApplyWithdrawResponse> {
  return try {
   val url = "$baseUrl/users/apply-withdraw?amount=$amount"
   println("NetworkService - 请求申请提现: $url") // 日志输出
   println("NetworkService - 提现金额: $amount") // 日志输出

   val response = client.post(url) {
    header("Authorization", "Bearer $token")
   }.body<String>()  // 先获取原始响应

   println("NetworkService - 提现申请原始响应: $response") // 日志输出完整响应

   // 手动解析JSON
   val parsedResponse = try {
    kotlinx.serialization.json.Json {
     ignoreUnknownKeys = true
     coerceInputValues = true
    }.decodeFromString<ApplyWithdrawResponse>(response)
   } catch (e: Exception) {
    println("NetworkService - JSON解析失败: ${e.message}") // 日志输出
    println("NetworkService - 原始JSON: $response") // 日志输出
    throw e
   }

   println("NetworkService - 提现申请响应: code=${parsedResponse.code}, message=${parsedResponse.message}") // 日志输出

   if (parsedResponse.code == 200) {
    println("NetworkService - 提现申请成功: ${parsedResponse.message}") // 日志输出
   } else {
    // 去除 "Error: " 前缀
    val cleanMessage = parsedResponse.message?.removePrefix("Error: ") ?: "提现申请失败"
    println("NetworkService - 提现申请失败: $cleanMessage") // 日志输出
   }

   Result.success(parsedResponse)
  } catch (e: Exception) {
   println("NetworkService - 申请提现异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }
 /**
  * 获取用户收益记录
  * GET /users/income-records
  * @param token 用户Token
  * @param page 页码（默认1）
  * @param pageSize 每页数量（默认10）
  * @return Result<IncomeRecordResponse> 收益记录结果
  */
 suspend fun getIncomeRecords(token: String, page: Int = 1, pageSize: Int = 10): Result<IncomeRecordResponse> {
  return try {
   val url = "$baseUrl/users/income-records?page=$page&pageSize=$pageSize"
   println("NetworkService - 请求收益记录: $url, 页码: $page, 每页: $pageSize") // 日志输出

   val response = client.get(url) {
    header("Authorization", "Bearer $token")
   }.body<String>()  // 先获取原始响应

   println("NetworkService - 收益记录原始响应: $response") // 日志输出完整响应

   // 手动解析JSON
   val parsedResponse = try {
    kotlinx.serialization.json.Json {
     ignoreUnknownKeys = true
     coerceInputValues = true
    }.decodeFromString<IncomeRecordResponse>(response)
   } catch (e: Exception) {
    println("NetworkService - JSON解析失败: ${e.message}") // 日志输出
    println("NetworkService - 原始JSON: $response") // 日志输出
    throw e
   }

   if (parsedResponse.code == 200 && parsedResponse.data != null) {
    println("NetworkService - 收益记录获取成功，第${page}页共 ${parsedResponse.data.size} 条记录") // 日志输出
   } else {
    val errorMsg = parsedResponse.message ?: "收益记录获取失败或无数据"
    println("NetworkService - $errorMsg") // 日志输出
   }

   Result.success(parsedResponse)
  } catch (e: Exception) {
   println("NetworkService - 获取收益记录异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 获取邀请用户列表
  * GET /users/invited
  * @param token 用户Token
  * @return Result<InvitedUsersResponse> 邀请用户列表结果
  */
 suspend fun getInvitedUsers(token: String): Result<InvitedUsersResponse> {
  return try {
   val url = "$baseUrl/users/invited"
   println("NetworkService - 请求邀请用户列表: $url") // 日志输出

   val response = client.get(url) {
    header("Authorization", "Bearer $token")
   }.body<String>()  // 先获取原始响应

   println("NetworkService - 邀请用户列表原始响应: $response") // 日志输出完整响应

   // 手动解析JSON
   val parsedResponse = try {
    kotlinx.serialization.json.Json {
     ignoreUnknownKeys = true
     coerceInputValues = true
    }.decodeFromString<InvitedUsersResponse>(response)
   } catch (e: Exception) {
    println("NetworkService - JSON解析失败: ${e.message}") // 日志输出
    println("NetworkService - 原始JSON: $response") // 日志输出
    throw e
   }

   if (parsedResponse.code == 200 && parsedResponse.data != null) {
    println("NetworkService - 邀请用户列表获取成功，共 ${parsedResponse.data.size} 个用户") // 日志输出
   } else {
    val errorMsg = parsedResponse.message ?: "邀请用户列表获取失败或无数据"
    println("NetworkService - $errorMsg") // 日志输出
   }

   Result.success(parsedResponse)
  } catch (e: Exception) {
   println("NetworkService - 获取邀请用户列表异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 获取收入排行榜
  * GET /users/income-ranking
  * @param token 用户Token
  * @return Result<RankingResponse> 排行榜结果
  */
 suspend fun getIncomeRanking(token: String): Result<RankingResponse> {
  return try {
   val url = "$baseUrl/users/income-ranking"
   println("NetworkService - 请求收入排行榜: $url") // 日志输出

   val response = client.get(url) {
    header("Authorization", "Bearer $token")
   }.body<String>()  // 先获取原始响应

   println("NetworkService - 排行榜原始响应: $response") // 日志输出完整响应

   // 手动解析JSON
   val parsedResponse = try {
    kotlinx.serialization.json.Json {
     ignoreUnknownKeys = true
     coerceInputValues = true
    }.decodeFromString<RankingResponse>(response)
   } catch (e: Exception) {
    println("NetworkService - JSON解析失败: ${e.message}") // 日志输出
    println("NetworkService - 原始JSON: $response") // 日志输出
    throw e
   }

   if (parsedResponse.code == 200 && parsedResponse.data != null) {
    val rankingCount = parsedResponse.data.size
    println("NetworkService - 排行榜获取成功，共 $rankingCount 条数据") // 日志输出
   } else {
    println("NetworkService - 排行榜获取失败或无数据") // 日志输出
   }

   Result.success(parsedResponse)
  } catch (e: Exception) {
   println("NetworkService - 获取排行榜异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 获取提现记录列表
  * GET /users/withdrawal-records
  * @param token 用户Token
  * @return Result<WithdrawRecordListResponse> 提现记录列表结果
  */
 suspend fun getWithdrawalRecords(token: String): Result<WithdrawRecordListResponse> {
  return try {
   val url = "$baseUrl/users/withdrawal-records"
   println("NetworkService - 请求提现记录列表: $url") // 日志输出

   val response = client.get(url) {
    header("Authorization", "Bearer $token")
   }.body<String>()  // 先获取原始响应

   println("NetworkService - 提现记录原始响应: $response") // 日志输出完整响应

   // 手动解析JSON
   val parsedResponse = try {
    kotlinx.serialization.json.Json {
     ignoreUnknownKeys = true
     coerceInputValues = true
    }.decodeFromString<WithdrawRecordListResponse>(response)
   } catch (e: Exception) {
    println("NetworkService - JSON解析失败: ${e.message}") // 日志输出
    println("NetworkService - 原始JSON: $response") // 日志输出
    throw e
   }

   if (parsedResponse.code == 200 && parsedResponse.data != null) {
    val recordCount = parsedResponse.data.size
    println("NetworkService - 提现记录获取成功，共 $recordCount 条数据") // 日志输出
   } else {
    println("NetworkService - 提现记录获取失败或无数据") // 日志输出
   }

   Result.success(parsedResponse)
  } catch (e: Exception) {
   println("NetworkService - 获取提现记录异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }
 /**
  * 支付宝支付接口
  * POST /payment/epay/create
  * @param productId 商品ID
  * @param paymentType 支付类型（alipay）
  * @param quantity 数量（默认1）
  * @param token 用户Token
  * @return Result<AlipayResponse> 支付宝支付结果
  */
 suspend fun createAlipayOrder(
  productId: Int,
  paymentType: String = "alipay",
  quantity: Int = 1,
  token: String
 ): Result<AlipayResponse> {
  return try {
   val url = "$baseUrl/payment/epay/create"
   println("NetworkService - 请求支付宝支付: $url") // 日志输出
   println("NetworkService - 参数: productId=$productId, paymentType=$paymentType, quantity=$quantity") // 日志输出

   // 先获取原始字符串响应
   val rawResponse = client.post(url) {
    header("Authorization", "Bearer $token")
    header("Content-Type", "application/x-www-form-urlencoded")
    setBody("productId=$productId&paymentType=$paymentType&quantity=$quantity")
   }.body<String>()

   println("NetworkService - 支付宝原始响应: $rawResponse") // 打印原始JSON

   // 解析响应
   val response = kotlinx.serialization.json.Json {
    ignoreUnknownKeys = true
    coerceInputValues = true
   }.decodeFromString<AlipayResponse>(rawResponse)

   println("NetworkService - 支付宝响应: code=${response.code}, message=${response.message}, msg=${response.msg}") // 日志输出
   println("NetworkService - data: ${response.data}") // 日志输出
   println("NetworkService - trade_no=${response.trade_no}, qrcode=${response.qrcode}") // 日志输出

   // 支持两种响应格式：
   // 1. 标准RESTful格式: code=200, data={qrcode: "..."}
   // 2. 易支付格式: code=1, qrcode="..."
   val isSuccess = (response.code == 200 && response.data != null) || (response.code == 1)
   val qrcodeUrl = response.data?.qrcode ?: response.qrcode

   if (isSuccess && qrcodeUrl != null) {
    println("NetworkService - 支付宝订单创建成功") // 日志输出
    println("NetworkService - 二维码URL: $qrcodeUrl") // 日志输出
   } else {
    val errorMsg = response.message ?: response.msg ?: "未知错误"
    println("NetworkService - 支付宝订单创建失败: $errorMsg") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 支付宝订单创建异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 提交商品投诉
  * POST /products/complaint
  * 参数通过Query传递：productId, productName, productPrice, complaintReason, userId(可选), userName(可选)
  * @param productId 商品ID
  * @param productName 商品名称
  * @param productPrice 商品价格
  * @param complaintReason 投诉理由
  * @param userId 用户ID（可选）
  * @param userName 用户名（可选）
  * @return Result<ComplaintResponse> 投诉提交结果
  */
 suspend fun submitComplaint(
  productId: String,
  productName: String,
  productPrice: Double,
  complaintReason: String,
  userId: Int? = null,
  userName: String? = null
 ): Result<ComplaintResponse> {
  return try {
   // 构建URL，包含必需参数和可选参数
   var url = "$baseUrl/products/complaint?productId=$productId&productName=$productName&productPrice=$productPrice&complaintReason=$complaintReason"
   
   // 添加可选参数
   if (userId != null) {
    url += "&userId=$userId"
   }
   if (userName != null) {
    url += "&userName=$userName"
   }
   
   println("NetworkService - 提交商品投诉: $url")
   println("NetworkService - 参数: productId=$productId, productName=$productName, price=$productPrice")
   println("NetworkService - 投诉理由: $complaintReason")

   val response = client.post(url) {
    // 参数通过URL传递，不需要设置Body
   }.body<ComplaintResponse>()

   if (response.code == 200) {
    println("NetworkService - 投诉提交成功！")
    println("NetworkService - 响应信息: ${response.message}")
   } else {
    println("NetworkService - 投诉提交失败: ${response.message}")
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 提交投诉异常: ${e.message}")
   e.printStackTrace()
   Result.failure(e)
  }
 }
 
 /**
  * 升级管理员支付接口
  * POST /payment/upgrade-admin
  * @param token 用户Token
  * @return Result<AlipayResponse> 支付宝支付结果
  */
 suspend fun upgradeToAdmin(token: String): Result<AlipayResponse> {
  return try {
   val url = "$baseUrl/payment/upgrade-admin"
   println("NetworkService - 请求升级管理员支付: $url") // 日志输出

   // 先获取原始字符串响应
   val rawResponse = client.post(url) {
    header("Authorization", "Bearer $token")
   }.body<String>()

   println("NetworkService - 升级管理员原始响应: $rawResponse") // 打印原始JSON

   // 解析响应
   val response = kotlinx.serialization.json.Json {
    ignoreUnknownKeys = true
    coerceInputValues = true
   }.decodeFromString<AlipayResponse>(rawResponse)

   println("NetworkService - 升级管理员响应: code=${response.code}, message=${response.message}") // 日志输出
   println("NetworkService - data: ${response.data}") // 日志输出

   if (response.code == 200) {
    println("NetworkService - 升级管理员订单创建成功") // 日志输出
    val qrcodeImg = response.data?.img ?: ""
    println("NetworkService - 二维码图片URL: $qrcodeImg") // 日志输出
   } else {
    val errorMsg = response.message ?: "未知错误"
    println("NetworkService - 升级管理员订单创建失败: $errorMsg") // 日志输出
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 升级管理员订单创建异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 检查升级管理员支付状态接口
  * GET /users/check-upgrade-status
  * 用于前端轮询检查升级支付是否成功
  * 通过检查用户角色是否已更新来判断支付状态
  * @param token 用户Token
  * @return Result<Map<String, Any?>> 检查结果
  */
 suspend fun checkUpgradeStatus(token: String): Result<Map<String, Any?>> {
  return try {
   val url = "$baseUrl/users/check-upgrade-status"
   println("NetworkService - 请求检查升级状态: $url") // 日志输出

   val response = client.get(url) {
    header("Authorization", "Bearer $token")
    addDeviceId()
   }.body<String>()

   println("NetworkService - 检查升级状态原始响应: $response") // 日志输出

   // 手动解析JSON
   val parsedResponse = try {
    kotlinx.serialization.json.Json {
     ignoreUnknownKeys = true
     coerceInputValues = true
    }.decodeFromString<kotlinx.serialization.json.JsonObject>(response)
   } catch (e: Exception) {
    println("NetworkService - JSON解析失败: ${e.message}") // 日志输出
    throw e
   }

   // 转换为 Map<String, Any?>
   val resultMap = mutableMapOf<String, Any?>()
   parsedResponse.forEach { (key, jsonElement) ->
    resultMap[key] = when (jsonElement) {
     is kotlinx.serialization.json.JsonPrimitive -> {
      when {
       jsonElement.isString -> jsonElement.content
       jsonElement.content.equals("true", ignoreCase = true) -> true
       jsonElement.content.equals("false", ignoreCase = true) -> false
       else -> {
        // 尝试解析为数字
        jsonElement.content?.let { content ->
         content.toDoubleOrNull()?.let { doubleValue ->
          if (doubleValue == doubleValue.toLong().toDouble()) {
           doubleValue.toLong().toInt() // 整数
          } else {
           doubleValue // 浮点数
          }
         } ?: content // 字符串
        }
       }
      }
     }
     is kotlinx.serialization.json.JsonObject -> {
      jsonElement.toMap().mapValues { (_, value) ->
       when (value) {
        is kotlinx.serialization.json.JsonPrimitive -> {
         when {
          value.isString -> value.content
          value.content.equals("true", ignoreCase = true) -> true
          value.content.equals("false", ignoreCase = true) -> false
          else -> {
           // 尝试解析为数字
           value.content?.let { content ->
            content.toDoubleOrNull()?.let { doubleValue ->
             if (doubleValue == doubleValue.toLong().toDouble()) {
              doubleValue.toLong().toInt() // 整数
             } else {
              doubleValue // 浮点数
             }
            } ?: content // 字符串
           }
          }
         }
        }
        else -> value.toString()
       }
      }
     }
     else -> jsonElement.toString()
    }
   }

   if ((resultMap["code"] as? Int) == 200) {
    val data = resultMap["data"] as? Map<*, *>
    val isUpgraded = data?.get("isUpgraded") as? Boolean ?: false
    val currentRole = data?.get("currentRole") as? String ?: "user"
    println("NetworkService - 检查升级状态成功: isUpgraded=$isUpgraded, currentRole=$currentRole") // 日志输出
   } else {
    val message = resultMap["message"] as? String ?: "未知错误"
    println("NetworkService - 检查升级状态失败: $message") // 日志输出
   }

   Result.success(resultMap)
  } catch (e: Exception) {
   println("NetworkService - 检查升级状态异常: ${e.message}") // 日志输出
   e.printStackTrace()
   Result.failure(e)
  }
 }

 /**
  * 上传商品接口
  * POST /products/upload
  * 需要Token验证和管理员权限
  * @param name 商品名称
  * @param linkUrl 资源链接地址
  * @param category 商品分类
  * @param remark 备注信息
  * @param token 用户Token
  * @return Result<UploadProductResponse> 上传结果
  */
 suspend fun uploadProduct(
  name: String,
  linkUrl: String,
  category: String,
  remark: String?,
  token: String
 ): Result<UploadProductResponse> {
  return try {
   val uploadUrl = "$baseUrl/products/upload"
   println("NetworkService - 请求上传商品: $uploadUrl")
   println("NetworkService - 商品信息: name=$name, category=$category")

   // 构建请求体
   val request = UploadProductRequest(
    name = name,
    linkUrl = linkUrl,
    category = category,
    remark = remark,
    description = remark ?: "",
    coverImageUrl = null,
    price = 0.0,
    status = 0,
    tags = null,
    sortOrder = 0
   )

   val httpResponse: HttpResponse = client.post(uploadUrl) {
    contentType(ContentType.Application.Json)
    header("Authorization", "Bearer $token")
    setBody(request)
   }

   // 检查401错误
   checkUnauthorized(httpResponse)

   val response = httpResponse.body<UploadProductResponse>()

   if (response.code == 200) {
    println("NetworkService - 商品上传成功！商品ID: ${response.data?.productId}")
   } else {
    println("NetworkService - 商品上传失败: ${response.message}")
   }

   Result.success(response)
  } catch (e: Exception) {
   println("NetworkService - 上传商品异常: ${e.message}")
   e.printStackTrace()
   Result.failure(e)
  }
 }
}